import React from 'react';
import PropTypes from 'prop-types';
import { mobile } from 'yola-bowser';
import classNames from 'classnames';
import { hdrm, i18next, utils } from '@yola/ws-sdk';
import { Modal, Panel, PanelGroup, designSystem } from '@yola/ws-ui';
import { contrastRatio, HSLColor } from '@yola/yola-palette-generator';
import detectModalWidth from '../helpers/detect-modal-width';
import sizes from '../constants/color-settings-sizes';
import constants from '../constants';
import customColorOptions from '../constants/custom-color-options';
import supportedColorModes from '../constants/supported-color-modes';
import ColorListPanel from './color-list-panel';
import ColorPanel from './color-panel';

const {
  DialogButton,
  DialogButtonGroup,
  DialogButtonGroupSeparator,
  DialogHeader,
  DialogHeaderTitle,
  ColorPicker,
  ColorCircle,
  HorizontalStack,
  Divider,
  Box,
  Stack,
  Heading,
  TabSelector,
  GradientPicker,
} = designSystem;

const { DEFAULT_OFFSET_Y, DEFAULT_OFFSET_X } = constants;

const defaultPredefinedColors = [];
const defaultPredefinedGradients = [];

const WHITE = new HSLColor(0, 0, 1);

const checkContrastRatio = (colorStopValue) => {
  const [h, s, l, a] = colorStopValue.map(Number);
  const color = new HSLColor(h, s, l, a);

  return contrastRatio(color, WHITE) >= customColorOptions.ACCEPTABLE_LIGHT_CR;
};

const ColorSettingsDialog = ({
  color,
  onColorChange,
  onCancel,
  onSubmit,
  offsetX = DEFAULT_OFFSET_X,
  offsetY = DEFAULT_OFFSET_Y,
  captions,
  displayWarning = false,
  onColorChangeComplete = utils.noop,
  onDragEnd,
  overlay = 'none',
  enableAlpha = false,
  supportedModes = [supportedColorModes.SOLID],
  mode = supportedColorModes.SOLID,
  gradientModel = null,
  gradientPickerKey = undefined,
  gradientBaseColor = null,
  onModeChange = utils.noop,
  onGradientChange = utils.noop,
  onGradientChangeEnd = utils.noop,
  onGradientColorStopClick = utils.noop,
  predefinedColors = defaultPredefinedColors,
  predefinedGradients = defaultPredefinedGradients,
  onPredefinedColorClick = utils.noop,
  onPredefinedGradientClick = utils.noop,
  onGradientBaseColorClick = utils.noop,
}) => {
  const modalWidth = detectModalWidth();
  const isGradientSupported = supportedModes.includes(supportedColorModes.GRADIENT);

  const attributes = { [hdrm.constants.attributes.DISABLE_BLUR_ATTR]: true };

  return (
    <Modal
      handleSubmit={onSubmit}
      handleCancel={onCancel}
      height="auto"
      attributes={attributes}
      width={modalWidth}
      offsetX={offsetX}
      offsetY={offsetY}
      draggable={!mobile}
      fullscreen={mobile}
      overlay={overlay}
      resizable={false}
      onDragEnd={onDragEnd}
      dragHandleSelector=".ws-color-settings-header"
    >
      <PanelGroup height="100%">
        <Panel
          height={sizes.MODAL_HEADER_HEIGHT}
          align="middle"
          corners="squared"
          style={{ flexShrink: '0' }}
          className="ws-color-settings-header"
        >
          <DialogHeader>
            <DialogHeaderTitle>{captions.title}</DialogHeaderTitle>
          </DialogHeader>
        </Panel>

        <Panel height="100%" scrollable style={{ borderBottom: 'none', position: 'relative' }}>
          {isGradientSupported && (
            <Box
              paddingLeft="spacing-m"
              paddingRight="spacing-m"
              paddingTop="spacing-s"
              paddingBottom="spacing-2-xs"
            >
              <TabSelector
                activeTabId={mode}
                tabs={[
                  {
                    id: supportedColorModes.SOLID,
                    label: i18next.t('Solid'),
                  },
                  {
                    id: supportedColorModes.GRADIENT,
                    label: i18next.t('Gradient'),
                  },
                ]}
                onClick={onModeChange}
              />
            </Box>
          )}
          {mode === supportedColorModes.GRADIENT && (
            <>
              <Box paddingBottom="spacing-xs">
                <GradientPicker
                  key={gradientPickerKey}
                  defaultValue={gradientModel}
                  contrastRatioChecker={checkContrastRatio}
                  captions={captions.gradientPicker}
                  onChange={onGradientChange}
                  onChangeEnd={onGradientChangeEnd}
                  onActiveColorClick={onGradientColorStopClick}
                />
              </Box>
              {!!predefinedGradients.length && (
                <>
                  <Divider />
                  <ColorListPanel
                    title={i18next.t('Website gradients')}
                    items={predefinedGradients}
                    onSelect={onPredefinedGradientClick}
                  />
                </>
              )}
              <Divider />
              <ColorPanel
                title={i18next.t('Base color')}
                color={gradientBaseColor}
                opacity={1}
                popoverProps={{
                  width: 210,
                  placement: 'top-center',
                  content: i18next.t(
                    'Sets the main color for this section. Text, buttons, and other elements adapt to match it.'
                  ),
                }}
                onClick={onGradientBaseColorClick}
              />
            </>
          )}
          {mode === supportedColorModes.SOLID && (
            <>
              <Box
                paddingTop="spacing-m"
                paddingBottom="spacing-m"
                paddingLeft="spacing-m"
                paddingRight="spacing-m"
              >
                <ColorPicker
                  color={color}
                  onChange={onColorChange}
                  enableAlpha={enableAlpha}
                  onChangeComplete={onColorChangeComplete}
                  {...(enableAlpha && { alphaTooltipLabel: captions.opacity })}
                  {...(displayWarning && { notificationTitle: captions.warning })}
                />
              </Box>
              {!!predefinedColors.length && (
                <>
                  <Divider />
                  <Box
                    paddingTop="spacing-2-xs"
                    paddingBottom="spacing-s"
                    paddingLeft="spacing-m"
                    paddingRight="spacing-m"
                  >
                    <Stack gap="spacing-2-xs">
                      <Heading type="heading-6">{i18next.t('Website colors')}</Heading>
                      <div
                        className={classNames('ws-color-settings-used-colors', {
                          'ws-color-settings-used-colors--mobile': mobile,
                        })}
                      >
                        <HorizontalStack rowGap="spacing-3-xs" gap="spacing-3-xs" wrap>
                          {predefinedColors.map(({ value, opacity }) => (
                            <div
                              key={`${value}-${opacity}`}
                              className="ws-color-settings-used-colors__item"
                              onClick={() => {
                                onPredefinedColorClick({ hslString: value, opacity });
                              }}
                            >
                              <ColorCircle color={value} opacity={opacity} />
                            </div>
                          ))}
                        </HorizontalStack>
                      </div>
                    </Stack>
                  </Box>
                </>
              )}
            </>
          )}
        </Panel>
        <Panel style={{ flexShrink: '0' }}>
          <DialogButtonGroup>
            <DialogButton onClick={onSubmit}>{captions.apply}</DialogButton>
            <DialogButtonGroupSeparator />
            <DialogButton onClick={onCancel}>{captions.cancel}</DialogButton>
          </DialogButtonGroup>
        </Panel>
      </PanelGroup>
    </Modal>
  );
};

ColorSettingsDialog.propTypes = {
  color: PropTypes.shape({}).isRequired,
  onColorChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  gradientModel: PropTypes.shape({}),
  gradientBaseColor: PropTypes.string,
  gradientPickerKey: PropTypes.number,
  mode: PropTypes.string,
  onModeChange: PropTypes.func,
  onGradientChange: PropTypes.func,
  onGradientChangeEnd: PropTypes.func,
  onGradientColorStopClick: PropTypes.func,
  captions: PropTypes.shape({
    title: PropTypes.string.isRequired,
    warning: PropTypes.string,
    apply: PropTypes.string.isRequired,
    cancel: PropTypes.string.isRequired,
    opacity: PropTypes.string,
    gradientPicker: PropTypes.shape({
      colorStopControlTitle: PropTypes.string.isRequired,
      colorStopControlPopoverContent: PropTypes.string.isRequired,
      linearOptionLabel: PropTypes.string.isRequired,
      radialOptionLabel: PropTypes.string.isRequired,
      flipActionLabel: PropTypes.string.isRequired,
      deleteActionLabel: PropTypes.string.isRequired,
    }),
  }).isRequired,
  offsetX: PropTypes.number,
  offsetY: PropTypes.number,
  overlay: PropTypes.string,
  displayWarning: PropTypes.bool,
  enableAlpha: PropTypes.bool,
  onColorChangeComplete: PropTypes.func,
  onDragEnd: PropTypes.func.isRequired,
  predefinedColors: PropTypes.array,
  predefinedGradients: PropTypes.array,
  supportedModes: PropTypes.array,
  onPredefinedColorClick: PropTypes.func,
  onPredefinedGradientClick: PropTypes.func,
  onGradientBaseColorClick: PropTypes.func,
};

export default ColorSettingsDialog;
